import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { ResultsLayout } from 'components/layout/ResultsLayout'
import { useSearchAndFilter } from 'components/SearchAndFilter/useSearchAndFilter'
import { useGetApplicationsQuery } from 'hooks/queries/Customers/useGetApplicationsQuery'
import { usePaginationWithApi } from 'hooks/usePaginationWithApi'
import { ApplicationItems } from '../CustomersListV2/ApplicationItems'
import {
  CustomersV2FiltersModal,
  type CustomersV2Filters,
} from '../CustomersListV2/CustomersV2FiltersModal'
import { CUSTOMER_STATUS_VERIFIED } from '../helpers/constants'

const INITIAL_FILTERS: CustomersV2Filters = {}

interface LiveCustomersListProps {
  onRefetch?: (refetch: () => void) => void
}

export const LiveCustomersList = ({ onRefetch }: LiveCustomersListProps) => {
  const { t } = useTranslation()

  const { page, rowsPerPage, handleChangePage, handleChangeRowsPerPage, resetPage } =
    usePaginationWithApi()

  const {
    searchValue,
    appliedSearch,
    appliedFilters,
    hasSearchAndFilter,
    setSearchValue,
    setAppliedFilters,
    onResetAll,
  } = useSearchAndFilter('', INITIAL_FILTERS)

  const { data, isFetching, isError, refetch } = useGetApplicationsQuery({
    name: appliedSearch || undefined,
    page,
    size: rowsPerPage,
    statuses: [CUSTOMER_STATUS_VERIFIED],
    types: appliedFilters?.types,
  })

  useEffect(() => {
    onRefetch?.(refetch)
  }, [refetch, onRefetch])

  const applications = data?.content ?? []
  const totalElements = data?.totalElements ?? 0

  return (
    <ResultsLayout
      testId="live-customers-results"
      isLoading={isFetching}
      isError={isError}
      hasResults={!!applications.length}
      hasSearchAndFilter={hasSearchAndFilter}
      searchAndFilter={{
        search: {
          placeholder: t('customers.searchPlaceholder'),
          value: searchValue,
          onChange: value => {
            setSearchValue(value)
            resetPage()
          },
        },
        filters: [],
        onResetAll: () => {
          onResetAll()
          resetPage()
        },
        additionalActions: (
          <CustomersV2FiltersModal
            appliedFilters={appliedFilters}
            showStatusFilter={false}
            onApply={newFilters => {
              setAppliedFilters(prev => ({ ...prev, ...newFilters }))
              resetPage()
            }}
            onResetAll={() => {
              onResetAll()
              resetPage()
            }}
          />
        ),
      }}
      resetSearchAndFilters={() => {
        onResetAll()
        resetPage()
      }}
      refetchResults={refetch}
      emptyState={{
        dataName: 'customers',
        description: t('customers.description'),
      }}
      pagination={{
        page,
        rowsPerPage,
        total: totalElements,
        onChangePage: handleChangePage,
        onChangeRowsPerPage: handleChangeRowsPerPage,
      }}
    >
      <ApplicationItems applications={applications} />
    </ResultsLayout>
  )
}
